import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { fetchHomepageFeed } from '../../../services/homepageService';
import { resolveHomepageLocation } from '../../../services/locationService';
import './IndividualHomePage.css';
import { getTournamentOrganizerLabel } from '../../../utils/tournamentPresentation';

const MAX_ITEMS = 10;
const HOMEPAGE_DEBUG = process.env.REACT_APP_HOMEPAGE_DEBUG === 'true';

const formatLocation = (location = {}) => [location.city, location.state, location.country].filter(Boolean).join(', ');

const formatMatchStart = (value) => {
  if (!value) return 'Schedule to be announced';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Schedule to be announced';
  return parsed.toLocaleString();
};

const formatTournamentDate = (value) => {
  if (!value) return 'Dates to be announced';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Dates to be announced';
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const TeamBadge = ({ name, logo }) => (
  <div className="home-team-badge">
    {logo ? <img src={logo} alt={`${name} logo`} width="36" height="36" className="rounded-circle" /> : <span>{(name || 'T').slice(0, 1).toUpperCase()}</span>}
  </div>
);

const MatchCard = ({ match = {}, live = false, onOpen }) => (
  <article className={`home-card ${live ? 'home-card-live' : 'home-card-upcoming'}`}>
    <div className="home-card-glow" />
    <div className="home-card-content">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          <p className="home-kicker mb-1">{match.tournamentName || 'CrickArena Match'}</p>
          <h3 className="h5 mb-0 fw-bold">{match.team1} <span className="home-vs">vs</span> {match.team2}</h3>
        </div>
        <span className={`badge ${live ? 'home-badge-live' : 'home-badge-upcoming'}`}>{live ? 'LIVE' : 'UPCOMING'}</span>
      </div>

      <div className="home-teams-row">
        <div className="home-team-line"><TeamBadge name={match.team1} logo={match.team1Logo} /><span>{match.team1}</span></div>
        <div className="home-team-line"><TeamBadge name={match.team2} logo={match.team2Logo} /><span>{match.team2}</span></div>
      </div>

      <div className="home-match-meta">
        <span>{match.venue || 'Venue TBD'}{match.city ? `, ${match.city}` : ''}</span>
        {live ? <strong>{match.score || 'Score update pending'}{match.overs ? ` • ${match.overs} ov` : ''}</strong> : <strong>{formatMatchStart(match.matchStartTime)}</strong>}
      </div>

      <div className="d-flex gap-2 mt-2">
        {live && match.liveLink ? <a className="btn btn-sm btn-outline-light home-cta-secondary" href={match.liveLink} target="_blank" rel="noreferrer">Watch live</a> : null}
        <button type="button" className={`btn btn-sm fw-semibold ${live ? 'btn-danger' : 'btn-primary'} home-cta-primary`} onClick={() => onOpen(match)}>Open match</button>
      </div>
    </div>
  </article>
);

const TournamentCard = ({ tournament = {}, variant = 'ongoing' }) => {
  const organizerLabel = getTournamentOrganizerLabel(tournament);
  return (
  <article className={`home-card home-card-tournament ${variant === 'ongoing' ? 'home-card-tournament-ongoing' : 'home-card-tournament-upcoming'}`}>
    <div className="home-card-glow" />
    <div className="home-card-content">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <h3 className="h6 mb-0 fw-bold home-tournament-title">{tournament.name || 'Tournament'}</h3>
        <span className={`badge ${variant === 'ongoing' ? 'text-bg-success' : 'text-bg-info'}`}>{variant}</span>
      </div>
      {organizerLabel ? <p className="mb-1 text-muted small text-truncate" title={organizerLabel}>{organizerLabel}</p> : null}
      <div className="home-tournament-grid">
        <p className="mb-0">📍 {tournament.location || tournament.city || 'Location TBD'}</p>
        <p className="mb-0">👥 {tournament.teamCount || 0} teams</p>
        <p className="mb-0">🏏 {tournament.matchCount || tournament.matchesPlayed || 0} matches</p>
        <p className="mb-0">📅 {formatTournamentDate(tournament.startDate)}</p>
      </div>
    </div>
  </article>
  );
};

const Section = ({ title, subtitle, items = [], emptyText, renderItem, tone = 'default' }) => (
  <section className={`home-section home-section-${tone}`}>
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div>
        <h2 className="h5 mb-0 fw-bold">{title}</h2>
        <p className="small text-muted mb-0">{subtitle}</p>
      </div>
      <span className="badge home-count-badge">{items.length}/{MAX_ITEMS}</span>
    </div>

    {items.length === 0 ? <div className="alert alert-light border mb-0">{emptyText}</div> : null}

    {items.length > 0 ? (
      <div className="row g-3">
        {items.map((item) => (
          <div key={item.id} className="col-12 col-xl-6">{renderItem(item)}</div>
        ))}
      </div>
    ) : null}
  </section>
);

const IndividualHomePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [location, setLocation] = useState({ city: '', state: '', country: '', latitude: null, longitude: null, source: 'country' });
  const [feed, setFeed] = useState({
    liveMatches: [],
    upcomingMatches: [],
    ongoingTournaments: [],
    upcomingTournaments: [],
  });

  const locationLabel = useMemo(() => formatLocation(location) || 'your area', [location]);

  const loadHomepage = useCallback(async (signal) => {
    setIsLoading(true);
    setError('');

    try {
      const resolvedLocation = await resolveHomepageLocation();
      setLocation(resolvedLocation);

      const payload = await fetchHomepageFeed({
        ...resolvedLocation,
        signal,
      });
      setFeed(payload);

      if (HOMEPAGE_DEBUG) {
        // eslint-disable-next-line no-console
        console.debug('[homepage:web] mapped counts', {
          liveMatches: Array.isArray(payload?.liveMatches) ? payload.liveMatches.length : 0,
          upcomingMatches: Array.isArray(payload?.upcomingMatches) ? payload.upcomingMatches.length : 0,
          ongoingTournaments: Array.isArray(payload?.ongoingTournaments) ? payload.ongoingTournaments.length : 0,
          upcomingTournaments: Array.isArray(payload?.upcomingTournaments) ? payload.upcomingTournaments.length : 0,
        });
      }
    } catch (loadError) {
      if (loadError.name !== 'AbortError') {
        setError('Unable to refresh discovery feed. Please try again shortly.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadHomepage(abortController.signal);
    return () => abortController.abort();
  }, [loadHomepage]);

  const openMatch = (match) => {
    if (match?.tournamentId && match?.id) {
      navigate(`/public/tournaments/${match.tournamentId}/matches/${match.id}`);
    }
  };

  return (
    <PageShell containerClassName="p-0">
      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-body d-grid gap-4">
          <div className="rounded-4 p-4 text-white home-hero-banner">
            <p className="text-uppercase small fw-bold mb-1">CrickArena Discovery</p>
            <h1 className="h3 mb-1 fw-bold">Live near you</h1>
            <p className="mb-3 text-white-50">Showing cricket around {locationLabel} ({location.source || 'country'} based).</p>
            <button type="button" className="btn btn-light btn-sm fw-semibold" onClick={() => loadHomepage()} disabled={isLoading}>
              {isLoading ? 'Refreshing…' : 'Refresh feed'}
            </button>
          </div>

          {error ? <div className="alert alert-warning mb-0">{error}</div> : null}
          {isLoading ? <div className="alert alert-info mb-0">Loading premium discovery feed…</div> : null}


          <section className="home-section home-section-planned">
            <div className="home-card home-card-tournament">
              <div className="home-card-content">
                <p className="home-kicker mb-1">Quick street cricket</p>
                <h2 className="h5 fw-bold mb-2">Gali Cricket</h2>
                <p className="text-muted mb-3">Create, score, share, and resume casual street matches.</p>
                <button type="button" className="btn btn-primary fw-semibold" onClick={() => navigate('/gali')}>Gali Cricket</button>
              </div>
            </div>
          </section>

          {!isLoading ? (
            <>
              <Section title="Live Matches" subtitle="Top live action around you" tone="live" items={feed.liveMatches} emptyText="No live matches near you right now." renderItem={(item) => <MatchCard match={item} live onOpen={openMatch} />} />
              <Section title="Upcoming Matches" subtitle="Upcoming around your location" tone="upcoming" items={feed.upcomingMatches} emptyText="No upcoming matches near you right now." renderItem={(item) => <MatchCard match={item} onOpen={openMatch} />} />
              <Section title="Ongoing Tournaments" subtitle="Tournaments near your area" tone="ongoing" items={feed.ongoingTournaments} emptyText="No ongoing tournaments near you right now." renderItem={(item) => <TournamentCard tournament={item} variant="ongoing" />} />
              <Section title="Upcoming Tournaments" subtitle="Plan your next competition" tone="planned" items={feed.upcomingTournaments} emptyText="No upcoming tournaments near you right now." renderItem={(item) => <TournamentCard tournament={item} variant="upcoming" />} />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default IndividualHomePage;
